import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchShowreel } from '../lib/sanity';
import Lottie from 'lottie-react';
import playShowreelAnimation from '../assets/lottie_animations/play_showreel.json';
import playShowreelHoverAnimation from '../assets/lottie_animations/play_showreel_hover.json';
import playShowreelHoverOutAnimation from '../assets/lottie_animations/play_showreel_hover_out.json';
import playShowreelOutAnimation from '../assets/lottie_animations/play_showreel_out.json';
import './Showreel.css';

const Showreel = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showreelData, setShowreelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lottieData, setLottieData] = useState(null);
  const [hoverLottieData, setHoverLottieData] = useState(null);
  const [hoverOutLottieData, setHoverOutLottieData] = useState(null);
  const [clickOutLottieData, setClickOutLottieData] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [hasClicked, setHasClicked] = useState(false);
  const [timelineValue, setTimelineValue] = useState(0);
  const [showFade, setShowFade] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const loadShowreel = async () => {
      try {
        const data = await fetchShowreel();
        setShowreelData(data);
      } catch (error) {
        console.error('Error loading showreel:', error);
      } finally {
        setLoading(false);
      }
    };

    const loadLottieAnimations = async () => {
      try {
        // Import the animation data directly
        setLottieData(playShowreelAnimation);
        setHoverLottieData(playShowreelHoverAnimation);
        setHoverOutLottieData(playShowreelHoverOutAnimation);
        setClickOutLottieData(playShowreelOutAnimation);
      } catch (error) {
        console.error('Error loading Lottie animations:', error);
      }
    };

    loadShowreel();
    loadLottieAnimations();
  }, []);

  const handleVideoClick = () => {
    if (!showreelData?.fullVideoUrl) return;
    
    // If already playing full video, go back to preview
    if (isPlaying) {
      handleBackToPreview();
      return;
    }
    
    // Start fade transition
    setShowFade(true);
    
    // After fade completes, change video
    setTimeout(() => {
      setIsPlaying(true);
      setHasClicked(true);
      
      // Change video source to full video
      if (videoRef.current) {
        videoRef.current.src = showreelData.fullVideoUrl;
        videoRef.current.loop = false;
        videoRef.current.muted = false;
        videoRef.current.controls = false;
        
        // Load the video and set up event handlers
        videoRef.current.load();
        
        videoRef.current.onLoadedData = () => {
          // Start playing the video automatically
          videoRef.current.play().catch(error => {
            console.error('Error playing video:', error);
          });
        };

        videoRef.current.onPlay = () => {
          // Clear loading state when video starts playing
        };
        
        videoRef.current.onEnded = () => {
          handleBackToPreview();
        };
        
        // Separate timeupdate handler for timeline
        videoRef.current.addEventListener('timeupdate', handleTimeUpdate);
        
        // Force play attempt after a short delay to ensure video plays
        setTimeout(() => {
          if (videoRef.current && videoRef.current.paused) {
            videoRef.current.play().catch(error => {
              console.error('Error playing video after delay:', error);
            });
          }
        }, 100);
        
        // Hide fade after video is loaded and ready
        setTimeout(() => {
          setShowFade(false);
        }, 500);
      }
    }, 300); // Fade duration
  };

  const resetToPreviewState = () => {
    setIsPlaying(false);
    setHasClicked(false);
    setHasPlayedOnce(false);
    setIsHovered(false);
    setShowFade(false);
    setTimelineValue(0); // Reset timeline value when returning to preview
    
    // Reset timeline background
    const timelineSlider = document.getElementById('timeline');
    if (timelineSlider) {
      timelineSlider.style.background = 'linear-gradient(to right, #ff0000 0%, #ff0000 0%, rgba(255, 255, 255, 0.3) 0%)';
    }
    
    if (videoRef.current) {
      // Remove event listeners
      videoRef.current.onLoadedData = null;
      videoRef.current.onEnded = null;
      videoRef.current.onPlay = null;
      
      // Remove the timeupdate listener
      videoRef.current.removeEventListener('timeupdate', handleTimeUpdate);
      
      videoRef.current.src = showreelData.previewVideoUrl;
      videoRef.current.loop = true;
      videoRef.current.muted = true;
      videoRef.current.controls = false;
      videoRef.current.load();
      videoRef.current.play();
    }
  };

  // const handleFullVideoEnd = () => {
  //   resetToPreviewState();
  // };

  const handleBackToPreview = () => {
    resetToPreviewState();
  };

  const handleMouseEnter = () => {
    if (!hasClicked) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!hasClicked) {
      setIsHovered(false);
    }
  };

  const handleLottieComplete = () => {
    if (!hasPlayedOnce) {
      setHasPlayedOnce(true);
    }
  };

  const handleTimelineUpdate = () => {
    if (videoRef.current && videoRef.current.duration) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setTimelineValue(progress);
      
      // Update the timeline slider background to show red progress
      const timelineSlider = document.getElementById('timeline');
      if (timelineSlider) {
        timelineSlider.style.background = `linear-gradient(to right, #ff0000 0%, #ff0000 ${progress}%, rgba(255, 255, 255, 0.3) ${progress}%)`;
      }
    }
  };

  const handleTimelineSeek = (e) => {
    if (videoRef.current && videoRef.current.duration) {
      const seekTime = (e.target.value / 100) * videoRef.current.duration;
      videoRef.current.currentTime = seekTime;
    }
  };

  const handleTimeUpdate = () => {
    handleTimelineUpdate();
    
    // Check if video is near end
    if (videoRef.current && videoRef.current.duration) {
      const timeLeft = videoRef.current.duration - videoRef.current.currentTime;
      if (timeLeft < 0.5) { // Less than 0.5 seconds left
        handleBackToPreview();
      }
    }
  };

  useEffect(() => {
    if (videoRef.current && showreelData?.previewVideoUrl) {
      videoRef.current.play();
    }
  }, [showreelData]);

  // Don't render if no showreel data is available
  if (loading) {
    return (
      <div className="showreel-container">
        <div className="loading-placeholder">Loading showreel...</div>
      </div>
    );
  }

  if (!showreelData) {
    return null; // Don't render anything if no showreel data
  }

  // Determine which animation to show
  let currentAnimation = lottieData;
  let shouldLoop = false;
  let shouldAutoplay = true;

  if (hasClicked) {
    currentAnimation = clickOutLottieData;
    shouldLoop = false;
  } else if (hasPlayedOnce) {
    if (isHovered) {
      currentAnimation = hoverLottieData;
      shouldLoop = false;
    } else {
      currentAnimation = hoverOutLottieData;
      shouldLoop = false;
    }
  }

  return (
    <div className="showreel-container" ref={containerRef}>
      {/* Video Container */}
      <motion.div 
        className="showreel-video-container"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <video
          ref={videoRef}
          className="showreel-video"
          muted
          loop
          playsInline
          onClick={handleVideoClick}
        >
          <source src={showreelData.previewVideoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Animation Overlay - Always visible */}
        <div 
          className="animation-overlay"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleVideoClick}
        >
          <div className="play-button">
            {currentAnimation && (
              <Lottie
                animationData={currentAnimation}
                className="play-icon"
                loop={shouldLoop}
                autoplay={shouldAutoplay}
                onComplete={handleLottieComplete}
              />
            )}
          </div>
        </div>

        {/* White Fade Overlay */}
        <AnimatePresence>
          {showFade && (
            <motion.div 
              className="white-fade-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
          )}
        </AnimatePresence>

        {/* Full Video Overlay */}
        <AnimatePresence>
          {isPlaying && (
            <motion.div 
              className="full-video-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="timeline-container">
                <input 
                  type="range" 
                  id="timeline" 
                  min="0" 
                  max="100" 
                  value={timelineValue}
                  onChange={handleTimelineSeek}
                  className="timeline-slider"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Showreel; 